import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes as Switch, Route } from 'react-router-dom'
import { PrivateRoute, PublicRoute, ParamsRoute } from 'components/index'
import routes from 'utils/routes'
import { SENTRY_TAG, ErrorBoundary } from '@hotmart/hub-components'

import '../app-cosmos'
import '@hotmart-org-ca/cosmos-web/dist/web-components/cosmos-web/cosmos-web.css'
import '@hotmart-org-ca/cosmos-tokens/dist/hotmart-product/base/css/_variables.css'

const MyAccount = lazy(() => import('my_account/Main'))
const Profile = lazy(() => import('my_account/Profile'))
const ProfilePassword = lazy(() => import('my_account/ProfilePassword'))
const ProfilePrivacy = lazy(() => import('my_account/ProfilePrivacy'))
const ProfileDevice = lazy(() => import('my_account/ProfileDevice'))
const MainPage = lazy(() => import('purchase/MainPage'))
const CreatorPage = lazy(() => import('components/CreatorPage'))
const PurchasesPage = lazy(() => import('purchase/PurchasesPage'))
const PurchaseModalRoute = lazy(() => import('purchase/PurchaseModalRoute'))
const PurchasesArchivedPage = lazy(() => import('purchase/PurchasesArchivedPage'))
const CasSurrogateAuth = lazy(() => import('../routes/casSurrogateAuth'))
const CheckinAuth = lazy(() => import('components/CheckinAuth'))
const CheckinUnauth = lazy(() => import('components/CheckinUnauth'))
const TicketUpgradePage = lazy(() => import('purchase/TicketUpgradePage'))
const Logout = lazy(() => import('components/Logout'))
const Structure = lazy(() => import('components/Structure'))
const PaymentManagementPage = lazy(() => import('payments_subscriptions/PaymentManagementPage'))
const PaymentDetailsPage = lazy(() => import('payments_subscriptions/PaymentDetailsPage'))
const PaymentViewPage = lazy(() => import('payments_subscriptions/PaymentViewPage'))
const ProcessingPaymentPage = lazy(() => import('payments_subscriptions/ProcessingPaymentPage'))
const AlterPaymentMethodPage = lazy(() => import('payments_subscriptions/AlterPaymentMethodPage'))
const AlterPaymentMethodPageCreditCardPage = lazy(
  () => import('payments_subscriptions/AlterPaymentMethodPageCreditCardPage')
)
const ChangeBillingDatePage = lazy(() => import('payments_subscriptions/ChangeBillingDatePage'))
const InvoicePage = lazy(() => import('purchase/InvoicePage'))
const Loader = lazy(() => import('@hotmart/hub-components/src/Loader'))
const Renew = lazy(() => import('components/Renew'))
const PurchasesCancelledPage = lazy(() => import('purchase/PurchasesCancelledPage'))
const RecurrencesHistoryPage = lazy(() => import('payments_subscriptions/RecurrencesHistoryPage'))
const Access = lazy(() => import('components/Access'))

import {
  useRegularizePaymentPageCollectionsRouter,
  useRegularizePaymentPageRouter
} from './RegularizePaymentPageRoutes'

function Routes() {
  const RegularizePaymentPageCollectionsRouter = useRegularizePaymentPageCollectionsRouter()
  const RegularizePaymentPageRouter = useRegularizePaymentPageRouter()

  return (
    <BrowserRouter>
      <ErrorBoundary sentryTag={SENTRY_TAG.HOST}>
        <Suspense>
          <Switch>
            <Route element={<Structure />}>
              <Route
                path={`${routes.purchase.main}`}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={MainPage} />}
              >
                <Route
                  path=":productId"
                  element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchaseModalRoute} isModal={true} />}
                />
              </Route>
              <Route
                path={`${routes.menu.creator}`}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={CreatorPage} />}
              />
              <Route
                path={`${routes.purchase.purchases}`}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchasesPage} />}
              >
                <Route
                  path=":productId"
                  element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchaseModalRoute} isModal={true} />}
                />
              </Route>
              <Route
                path={`${routes.menu.profile}`}
                element={<PrivateRoute sentryTag={SENTRY_TAG.MY_ACCOUNT} element={MyAccount} />}
              />
              <Route
                path={routes.profile.profile}
                element={<PrivateRoute sentryTag={SENTRY_TAG.MY_ACCOUNT} element={Profile} />}
              />
              <Route
                path={routes.profile.password}
                element={<PrivateRoute sentryTag={SENTRY_TAG.MY_ACCOUNT} element={ProfilePassword} />}
              />
              <Route
                path={routes.profile.privacy}
                element={<PrivateRoute sentryTag={SENTRY_TAG.MY_ACCOUNT} element={ProfilePrivacy} />}
              />
              <Route
                path={routes.profile.device}
                element={<PrivateRoute sentryTag={SENTRY_TAG.MY_ACCOUNT} element={ProfileDevice} />}
              />
              <Route
                path={routes.event.ticketUpgrade}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={TicketUpgradePage} />}
              />
              <Route
                path={`${routes.paymentsSubscription.root}`}
                element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={PaymentManagementPage} />}
              >
                <Route
                  index
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={PaymentDetailsPage} />}
                />
                <Route
                  path={routes.paymentsSubscription.paymentView}
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={PaymentViewPage} />}
                />
                <Route
                  path={routes.paymentsSubscription.processingPayment}
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={ProcessingPaymentPage} />}
                />
                <Route
                  path={routes.paymentsSubscription.alterPaymentMethod}
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={AlterPaymentMethodPage} />}
                >
                  <Route
                    path={routes.paymentsSubscription.alterPaymentMethodCreditCard}
                    element={
                      <PrivateRoute
                        sentryTag={SENTRY_TAG.SUBSCRIPTION}
                        element={AlterPaymentMethodPageCreditCardPage}
                      />
                    }
                  />
                </Route>
                <Route
                  path={routes.paymentsSubscription.changeBillingDate}
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={ChangeBillingDatePage} />}
                />
                <Route
                  path={routes.paymentsSubscription.recurrencesHistory}
                  element={<PrivateRoute sentryTag={SENTRY_TAG.SUBSCRIPTION} element={RecurrencesHistoryPage} />}
                />
                {RegularizePaymentPageCollectionsRouter}
                {RegularizePaymentPageRouter}
              </Route>
              <Route
                path={routes.event.checkinAuth}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={CheckinAuth} />}
              />
              <Route
                path={routes.purchase.purchasesArchived}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchasesArchivedPage} />}
              >
                <Route
                  path=":productId"
                  element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchaseModalRoute} isModal={true} />}
                />
              </Route>
              <Route
                path={routes.purchase.cancelled}
                element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchasesCancelledPage} />}
              >
                <Route
                  path=":productId"
                  element={<PrivateRoute sentryTag={SENTRY_TAG.PURCHASE} element={PurchaseModalRoute} isModal={true} />}
                />
              </Route>
              <Route path={routes.auth.logout} element={<Logout />} />
              <Route path={routes.auth.login} element={<Loader />} />
              <Route path={routes.auth.renew} element={<Renew />} />
              <Route path={routes.auth.surrogate} element={<CasSurrogateAuth />} />
            </Route>
            <Route
              path={routes.event.checkinUnauth}
              element={<PublicRoute sentryTag={SENTRY_TAG.PURCHASE} element={CheckinUnauth} />}
            />
            <Route
              path={routes.purchase.invoice}
              element={<PublicRoute sentryTag={SENTRY_TAG.PURCHASE} element={InvoicePage} />}
            />
            <Route
              path={routes.access.main}
              element={<PublicRoute sentryTag={SENTRY_TAG.PURCHASE} element={Access} />}
            />
            <Route path="*" element={<ParamsRoute to={routes.menu.main} />} />
          </Switch>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

export default Routes
